import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { RadioGroup, RadioGroupItem } from "@radix-ui/react-radio-group";
import { EmployeeDTO } from "@shaarp/common";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { Alert, AlertDescription } from "../ui/alert";
import { Button, buttonVariants } from "../ui/button";
import { useToast } from "../ui/use-toast";

export interface SignalPhoneDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: EmployeeDTO;
  companyId?: string;
  onReported: (motive: string) => void;
}

export function SignalPhoneDialog({
  open,
  onOpenChange,
  employee,
  onReported,
}: SignalPhoneDialogProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [motive, setMotive] = useState<string | undefined>(undefined);
  const api = useApi();
  const { toast } = useToast();

  const handleConfirm = async () => {
    if (!motive) return;
    setLoading(true);
    try {
      const endpoint =
        motive === "SIGNAL_WRONG"
          ? `/employee/${employee.id}/report/wrong-phone`
          : `/employee/${employee.id}/report/ban`;

      await api.post(endpoint, {
        reason: motive,
      });

      toast({
        title: t(
          "components.dataTableCompanyEmployees.phone.signal.successTitle",
        ),
        description: t(
          "components.dataTableCompanyEmployees.phone.signal.successDesc",
        ),
      });
      onReported(motive);
      onOpenChange(false);
    } catch (e) {
      toast({
        title: t(
          "components.dataTableCompanyEmployees.phone.signal.errorTitle",
        ),
        description: t(
          "components.dataTableCompanyEmployees.phone.signal.errorDesc",
        ),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const SIGNAL_MOTIVES = [
    {
      value: "SIGNAL_WRONG",
      label: t("components.dataTableCompanyEmployees.phone.signalWrong"),
    },
    {
      value: "SIGNAL_BAN",
      label: t("components.dataTableCompanyEmployees.phone.signalBan", {
        name: employee.firstName
          ? employee.firstName + " " + employee.lastName
          : "",
      }),
    },
  ];

  useEffect(() => {
    if (!open) {
      setMotive(undefined);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("components.dataTableCompanyEmployees.phone.signalTitle")}
          </DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey="components.dataTableCompanyEmployees.phone.signalDescription"
              components={{
                privacyPolicyLink: (
                  <a
                    href="https://shaarp.ai/legal/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:text-primary/80"
                  />
                ),
              }}
            />
          </DialogDescription>
        </DialogHeader>
        <div className="my-4">
          <RadioGroup
            value={motive}
            onValueChange={setMotive}
            className="flex flex-col gap-3"
            aria-label={t(
              "components.dataTableCompanyEmployees.phone.signalReasonLabel",
            )}
          >
            {SIGNAL_MOTIVES.map((m) => (
              <div key={m.value} className="flex flex-col gap-2">
                <RadioGroupItem
                  value={m.value}
                  id={m.value}
                  className={
                    buttonVariants({
                      variant: motive === m.value ? "default" : "outline",
                    }) + " px-3 py-2 rounded-md transition-colors"
                  }
                  style={{ userSelect: "none" }}
                >
                  {m.label}
                </RadioGroupItem>
              </div>
            ))}
          </RadioGroup>
        </div>
        {motive && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {motive === "SIGNAL_WRONG"
                ? t(
                    "components.dataTableCompanyEmployees.phone.signalWrongExplanation",
                  )
                : motive === "SIGNAL_BAN"
                  ? t(
                      "components.dataTableCompanyEmployees.phone.signalBanExplanation",
                      {
                        name: employee.firstName
                          ? employee.firstName + " " + employee.lastName
                          : "",
                      },
                    )
                  : null}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            disabled={loading || !motive}
            onClick={handleConfirm}
          >
            {loading
              ? t("common.loading")
              : t("components.dataTableCompanyEmployees.phone.signalConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
